
import React from 'react';
import { format } from 'date-fns';
import { Procurement } from '@/types/database.types';
import { CheckCircleIcon, ClockIcon, DollarSignIcon } from 'lucide-react';

interface PaymentTermsSectionProps {
  procurement: Procurement;
}

export const PaymentTermsSection: React.FC<PaymentTermsSectionProps> = ({
  procurement
}) => {
  console.log('PaymentTermsSection - procurement.finance:', procurement.finance);
  console.log('PaymentTermsSection - has_advance_payment:', procurement.finance?.has_advance_payment);
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Payment Terms</h3>
      
      {/* Basic Payment Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="p-3 border rounded-md">
          <p className="text-sm font-medium">Payment Type</p>
          <p className="text-sm capitalize">{procurement.finance?.payment_term_type || 'Upfront'}</p>
        </div>
        
        {procurement.finance?.payment_term_type === 'term' && (
          <div className="p-3 border rounded-md">
            <p className="text-sm font-medium">Payment Term Days</p>
            <p className="text-sm">{procurement.finance?.payment_term_days || 0} days</p>
          </div>
        )}
        
        {procurement.finance?.payment_due_date && (
          <div className="p-3 border rounded-md">
            <p className="text-sm font-medium">Payment Due Date</p>
            <p className="text-sm">
              {format(new Date(procurement.finance.payment_due_date), 'PP')}
            </p>
          </div>
        )}
        
        <div className="p-3 border rounded-md">
          <p className="text-sm font-medium">Damage Return</p>
          <p className="text-sm">
            {procurement.finance?.supports_damage_return
              ? `Yes (${procurement.finance.damage_return_percentage}%)`
              : 'Not supported'}
          </p>
        </div>
      </div>

      {/* Advance Payment Section */}
      {procurement.finance?.has_advance_payment && (
        <div className="border-l-4 border-blue-200 pl-4 space-y-4">
          <h4 className="text-md font-medium flex items-center">
            <DollarSignIcon className="mr-2 h-4 w-4 text-blue-600" />
            Advance Payment Details
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 border rounded-md bg-blue-50">
              <p className="text-sm font-medium mb-1">Advance Amount</p>
              <p className="text-lg font-semibold text-blue-600">
                Br {procurement.finance?.advance_payment_amount?.toFixed(2) || '0.00'}
              </p>
              <div className="flex items-center mt-1">
                {procurement.finance?.advance_payment_confirmed ? (
                  <>
                    <CheckCircleIcon className="h-3 w-3 text-green-600 mr-1" />
                    <span className="text-xs text-green-600">Confirmed</span>
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-3 w-3 text-amber-600 mr-1" />
                    <span className="text-xs text-amber-600">Pending</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="p-3 border rounded-md">
              <p className="text-sm font-medium mb-1">Remaining Amount</p>
              <p className="text-lg font-semibold">
                Br {procurement.finance?.remaining_payment_amount?.toFixed(2) || '0.00'}
              </p>
              <div className="flex items-center mt-1">
                {procurement.finance?.remaining_payment_confirmed ? (
                  <>
                    <CheckCircleIcon className="h-3 w-3 text-green-600 mr-1" />
                    <span className="text-xs text-green-600">Confirmed</span>
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-3 w-3 text-amber-600 mr-1" />
                    <span className="text-xs text-amber-600">Pending</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Advance Payment Confirmation Details */}
          {procurement.finance?.advance_payment_confirmed && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border rounded-md bg-green-50">
                <p className="text-sm font-medium mb-1">Advance Payment Date</p>
                <p className="text-sm">{procurement.finance?.advance_payment_date 
                  ? format(new Date(procurement.finance.advance_payment_date), 'PP')
                  : 'Not specified'}</p>
                {procurement.finance?.advance_payment_confirmed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Confirmed: {format(new Date(procurement.finance.advance_payment_confirmed_at), 'PP')}
                  </p>
                )}
              </div>
              
              {procurement.finance?.advance_payment_notes && (
                <div className="p-3 border rounded-md bg-green-50">
                  <p className="text-sm font-medium mb-1">Advance Payment Notes</p>
                  <p className="text-sm">{procurement.finance.advance_payment_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Remaining Payment Confirmation Details */}
          {procurement.finance?.remaining_payment_confirmed && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border rounded-md bg-green-50">
                <p className="text-sm font-medium mb-1">Remaining Payment Date</p>
                <p className="text-sm">{procurement.finance?.remaining_payment_date 
                  ? format(new Date(procurement.finance.remaining_payment_date), 'PP')
                  : 'Not specified'}</p>
                {procurement.finance?.remaining_payment_confirmed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Confirmed: {format(new Date(procurement.finance.remaining_payment_confirmed_at), 'PP')}
                  </p>
                )}
              </div>
              
              {procurement.finance?.remaining_payment_notes && (
                <div className="p-3 border rounded-md bg-green-50">
                  <p className="text-sm font-medium mb-1">Remaining Payment Notes</p>
                  <p className="text-sm">{procurement.finance.remaining_payment_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
